import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "../api/api";
import FeaturedLoader from "../components/Loaders/FeaturedLoader";


export default function BlogDetail() {
    const { slug } = useParams();
    const [blog, setBlog] = useState(null);
    const [latestBlogs, setLatestBlogs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBlog = async () => {
            setLoading(true);
            try {
                const res = await api.get(`/blogs/${slug}`);
                setBlog(res.data);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        const fetchLatest = async () => {
            try {
                const res = await api.get("/blogs");
                setLatestBlogs(res.data);
            } catch (err) {
                console.error(err);
            }
        };

        fetchBlog();
        fetchLatest();
    }, [slug]);

    if (loading) {
        return (
            <FeaturedLoader
                title="Loading blog article"
                subtitle="Fetching the latest content for you..."
            />
        );
    }

    if (!blog) {
        return <p className="text-center py-20 text-gray-700 dark:text-gray-300">Blog not found</p>;
    }

    const currentUrl = window.location.href;

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>{blog.meta_title || `${blog.title} | UAE Discounted Hotels`}</title>

                <meta
                    name="description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        `Read ${blog.title} on UAE Discounted Hotels. Find hotel deals, travel tips, and the latest updates across the UAE.`
                    }
                />

                {blog.meta_keywords && (
                    <meta name="keywords" content={blog.meta_keywords} />
                )}

                <meta name="robots" content="index, follow" />
                <meta name="author" content="UAE Discounted Hotels" />

                {/* Canonical */}
                <link
                    rel="canonical"
                    href={`https://uaediscountedhotels.com/blogs/${blog.slug}`}
                />

                {/* Open Graph */}
                <meta
                    property="og:title"
                    content={blog.meta_title || blog.title}
                />

                <meta
                    property="og:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        blog.title
                    }
                />

                <meta property="og:type" content="article" />

                <meta
                    property="og:url"
                    content={`https://uaediscountedhotels.com/blogs/${blog.slug}`}
                />

                <meta property="og:site_name" content="UAE Discounted Hotels" />
                <meta property="og:locale" content="en_US" />

                <meta
                    property="og:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://uaediscountedhotels.com/images/default-og.png"
                    }
                />

                {/* Article Meta */}
                {blog.created_at && (
                    <meta
                        property="article:published_time"
                        content={blog.created_at}
                    />
                )}

                {blog.updated_at && (
                    <meta
                        property="article:modified_time"
                        content={blog.updated_at}
                    />
                )}

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta
                    name="twitter:title"
                    content={blog.meta_title || blog.title}
                />
                <meta
                    name="twitter:description"
                    content={
                        blog.meta_description ||
                        blog.excerpt ||
                        blog.title
                    }
                />
                <meta
                    name="twitter:image"
                    content={
                        blog?.image
                            ? `https://uaediscountedhotels.com/uploads/blogs/${blog.image}`
                            : "https://uaediscountedhotels.com/images/default-og.png"
                    }
                />
            </Helmet>

            <div className="bg-gray-50 dark:bg-gray-950 text-gray-900 dark:text-gray-100">

                {/* ================= BANNER ================= */}
                <div className="relative h-[30vh] sm:h-[40vh] w-full mb-10">

                    <img
                        src={`https://uaediscountedhotels.com/uploads/blogs/banners/${blog.banner_image}`}
                        className="absolute inset-0 w-full h-full object-cover"
                        alt={blog.title}
                    />

                    <div className="absolute inset-0 bg-black/60" />

                    <div className="relative z-10 max-w-7xl mx-auto px-4 h-full flex flex-col justify-center text-white">

                        <span className="w-fit bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-xs px-3 py-1 rounded-full mb-3">
                            {blog.category?.name}
                        </span>

                        <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold max-w-3xl">
                            {blog.title}
                        </h1>

                        <p className="text-xs sm:text-sm text-gray-200 mt-2">
                            {new Date(blog.created_at).toDateString()}
                        </p>

                        <div className="text-xs sm:text-sm text-gray-300 mt-2">
                            <Link to="/" className="hover:text-white">Home</Link>
                            <span className="mx-2">/</span>
                            <Link to="/blogs" className="hover:text-white">Blogs</Link>
                            <span className="mx-2">/</span>
                            <span className="text-white">{blog.title}</span>
                        </div>

                    </div>
                </div>

                {/* ================= LAYOUT ================= */}
                <div className="max-w-7xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-3 gap-10">

                    {/* ================= MAIN ================= */}
                    <div className="lg:col-span-2">

                        <div className="flex gap-3 mb-4">
                            <span className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white text-xs px-3 py-1 rounded-full">
                                {blog.category?.name}
                            </span>

                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {new Date(blog.created_at).toDateString()}
                            </span>
                        </div>

                        {/* BLOG CONTENT */}
                        <div
                            className="
                    prose max-w-none
                    prose-headings:text-gray-900 dark:prose-headings:text-white
                    prose-p:text-gray-700 dark:prose-p:text-gray-300
                    prose-li:text-gray-700 dark:prose-li:text-gray-300
                    prose-strong:text-gray-900 dark:prose-strong:text-white
                    prose-a:text-blue-600 dark:prose-a:text-cyan-400
                    prose-blockquote:text-gray-600 dark:prose-blockquote:text-gray-400
                "
                            dangerouslySetInnerHTML={{ __html: blog.content }}
                        />

                        {/* ================= SHARE ================= */}
                        <div className="mt-10 border-t border-gray-200 dark:border-gray-800 pt-6">

                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Share this article
                            </h3>

                            <div className="flex flex-wrap gap-3">

                                <a href={`https://wa.me/?text=${encodeURIComponent(currentUrl)}`}
                                    className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg text-sm">
                                    WhatsApp
                                </a>

                                <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm">
                                    Facebook
                                </a>

                                <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}`}
                                    className="bg-black hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm">
                                    X (Twitter)
                                </a>

                                <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`}
                                    className="bg-blue-700 hover:bg-blue-800 text-white px-4 py-2 rounded-lg text-sm">
                                    LinkedIn
                                </a>

                                <a href={`https://t.me/share/url?url=${encodeURIComponent(currentUrl)}`}
                                    className="bg-sky-500 hover:bg-sky-600 text-white px-4 py-2 rounded-lg text-sm">
                                    Telegram
                                </a>

                                <button
                                    onClick={() => {
                                        navigator.clipboard.writeText(currentUrl);
                                        alert("Copied!");
                                    }}
                                    className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-2 rounded-lg text-sm"
                                >
                                    Copy Link
                                </button>

                            </div>
                        </div>

                    </div>

                    {/* ================= SIDEBAR ================= */}
                    <aside className="space-y-6">

                        {/* ABOUT */}
                        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                About This Blog
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                                Discover travel insights, guides, and tips.
                            </p>
                        </div>

                        {/* LATEST BLOGS */}
                        <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800 lg:sticky lg:top-24">

                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                                Latest Blogs
                            </h3>

                            <div className="space-y-4">

                                {latestBlogs.slice(0, 5).map((item) => (
                                    <Link
                                        key={item.id}
                                        to={`/blogs/${item.slug}`}
                                        className="flex gap-3 group"
                                    >

                                        <img
                                            src={item.thumbnail_image}
                                            className="w-14 h-14 object-cover rounded-md"
                                            alt={item.title || item.name || "Image"}
                                        />

                                        <div>

                                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 group-hover:text-blue-500">
                                                {item.title}
                                            </p>

                                            <span className="text-xs text-gray-400">
                                                {new Date(item.created_at).toDateString()}
                                            </span>

                                        </div>

                                    </Link>
                                ))}

                            </div>

                        </div>

                        {/* CTA */}
                        <div className="bg-gradient-to-r from-blue-600 to-cyan-500 text-white p-5 rounded-xl">

                            <h3 className="font-semibold text-lg">
                                Explore More
                            </h3>

                            <p className="text-sm mt-1">
                                Discover more blogs & travel guides
                            </p>

                            <Link
                                to="/blogs"
                                className="inline-block mt-3 bg-white text-blue-600 px-4 py-2 rounded-lg text-sm font-medium"
                            >
                                View All Blogs
                            </Link>

                        </div>

                    </aside>

                </div>

            </div>
        </>
    );
}